import React, { useState, useEffect } from 'react';
import { calculateRankings } from '../services/firebase/climbs';
// Import the SHARED ranking list styles
import '../styles/RankingsList.css';
// Remove the specific CSS import: import '../styles/BananaCupRankings.css';

interface BananaCupRankingsProps {
  gymId: string;
}

// Define the expected structure of the ranking data more explicitly
interface ClimberRanking {
    id: string;
    rank: number;
    name: string; // Now contains username (preferred) or display name as fallback
    routes: number; // Assuming this is the count of routes climbed
    points: number; // Assuming this is the banana points
}

const BananaCupRankings: React.FC<BananaCupRankingsProps> = ({ gymId }) => {
  // Use the more specific type for state
  const [rankings, setRankings] = useState<ClimberRanking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        setLoading(true);
        setError(''); // Clear previous errors
        console.log('Fetching rankings for gym:', gymId);
        // Cast the result to the expected type
        const rankingData = await calculateRankings(gymId) as ClimberRanking[];
        console.log('Ranking data received:', rankingData);
        setRankings(rankingData);
      } catch (err: any) {
        console.error('Error fetching rankings:', err);
        setError(err.message || 'Failed to load rankings');
      } finally {
        setLoading(false);
      }
    };

    if (gymId) { // Only fetch if gymId is valid
        fetchRankings();
    } else {
        setError('Gym ID is missing.');
        setLoading(false);
    }
  }, [gymId]);

  // --- Loading, Error, Empty States ---
  // Keep the specific loading/error styles for now, or use shared ones if available
  if (loading) {
    // You might want a shared loading component/style later
    return <div style={{ textAlign: 'center', padding: '20px', color: '#666', width: '100%' }}>Loading rankings...</div>;
  }

  if (error) {
    // You might want a shared error component/style later
    return <div style={{ backgroundColor: '#ffebee', color: '#d32f2f', padding: '10px 15px', border: '1px solid #f5c6cb', borderRadius: '4px', width: '100%', boxSizing: 'border-box', textAlign: 'center' }}>{error}</div>;
  }

  // Keep specific empty state styling for now
  if (rankings.length === 0) {
    return (
      <div className="empty-rankings" style={{ textAlign: 'center', padding: '30px', backgroundColor: '#f9f9f9', borderRadius: '8px', width: '100%', boxSizing: 'border-box' }}>
        <p style={{ fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>No rankings available yet.</p>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>Complete routes to start collecting bananas and appear in the rankings!</p>
      </div>
    );
  }

  // --- Render the List using shared styles ---
  return (
    // Use the shared container class
    <div className="rankings-list-container">
      {/* Use the shared scrollable container */}
      <div className="rankings-list-scrollable">
        {/* Use the shared header class */}
        <div className="rankings-list-header">
          {/* Use shared header column classes */}
          <div className="ranking-list-header-grade">Rank</div>
          <div className="ranking-list-header-setter">Climber</div>
          {/* Using 'rope' column for route count */}
          <div className="ranking-list-header-rope">Routes</div>
          {/* Using 'performance' column for points */}
          <div className="ranking-list-header-performance">Bananas</div>
        </div>

        {rankings.map((climber) => {
            // Determine background color for top 3 ranks
            let rankBgColor = 'white'; // Default
            let rankColor = 'black'; // Default text color
            if (climber.rank === 1) {
                rankBgColor = '#FFD700'; // Gold (more standard than yellow)
                rankColor = 'black';
            } else if (climber.rank === 2) {
                rankBgColor = '#C0C0C0'; // Silver
                rankColor = 'black';
            } else if (climber.rank === 3) {
                rankBgColor = '#CD7F32'; // Bronze
                rankColor = 'white';
            }

            return (
                // Use the shared item class
                <div key={climber.id} className="ranking-list-item">
                    {/* Use shared grade column for rank */}
                    <div className="ranking-list-item-grade">
                        {/* Use grade-circle for the rank number display */}
                        <div
                            className="grade-circle"
                            style={{
                                backgroundColor: rankBgColor,
                                color: rankColor,
                                border: '1px solid #ccc' // Add a subtle border
                            }}
                        >
                            {climber.rank}
                        </div>
                    </div>

                    {/* Use shared setter column for climber info */}
                    <div className="ranking-list-item-setter">
                         {/* Use shared setter-name class */}
                        <div className="setter-name">{climber.name}</div>
                        {/* Could add other details here if needed */}
                    </div>

                    {/* Use shared rope column for route count */}
                    <div className="ranking-list-item-rope">
                        {climber.routes}
                    </div>

                    {/* Use shared performance column for points */}
                    <div 
                        className="ranking-list-item-performance" 
                        style={{ 
                            fontWeight: 'bold', 
                            color: '#DAA520'
                        }}
                    >
                        <span>{climber.points}</span>
                        {/* Add banana emoji after the points */}
                        <span role="img" aria-label="banana">🍌</span>
                    </div>
                </div>
            );
        })}
      </div> {/* End scrollable */}
    </div> // End container
  );
};

export default BananaCupRankings;